import classNames from 'classnames';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransitionGroup } from 'react-transition-group';

import {
  requestVideoPlayerPause,
  updateVideoPlayerActiveCommentBucketIndex,
  updateVideoPlayerBucketId,
} from 'actions/videoPlayer';
import { SMALL_TABLET_MIN_WIDTH } from 'constants/breakpoints';
import { COMMENT_BUBBLE_TRANSITION_DURATION } from 'constants/comments';
import { videoPlayerShowComments } from 'creators/videoPlayer';
import { percentify } from 'utilities/videoPlayer';

import { selectFullscreen } from 'selectors/videoPlayer';

import CommentBubble from './comment-bubble';
import TimelineVideoIcon from './timeline-video-icon';
import {
  bucketInd,
  PERSIST_BUBBLE_RANGE,
} from '../../../../utilities/commentBuckets';

import MediaQuery from 'components/media-query';
import CommentFetch from 'components/ui-components/comment-fetch';

import styles from './styles.module.less';

const CommentTimeline = props => {
  const dispatch = useDispatch();
  const {
    duration,
    onSharePage,
    timelineIconClass,
    suppressActiveBubble,
  } = props;
  const commentBuckets = useSelector(
    state => state.video_player.comment_buckets
  );
  const activeBucketInd = useSelector(
    state => state.video_player.active_comment_bucket_index
  );
  const current_time = useSelector(state => state.video_player.current_time);
  const [hoveredInd, setHovered] = useState(-1);
  const [commentBarRef, setCommentBarRef] = useState(null);

  const setActiveBucketInd = index =>
    dispatch(updateVideoPlayerActiveCommentBucketIndex(index));

  const hoveredBucket = commentBuckets[hoveredInd];
  const newActiveBucketInd = bucketInd(current_time, duration);

  const newBucketIndValid = newActiveBucketInd in commentBuckets;
  const discardPrevBucket =
    newActiveBucketInd > activeBucketInd + PERSIST_BUBBLE_RANGE;

  if (newBucketIndValid || discardPrevBucket) {
    if (newActiveBucketInd != activeBucketInd && !suppressActiveBubble) {
      setActiveBucketInd(newActiveBucketInd);
    }
  }

  const activeBucket = commentBuckets[activeBucketInd];

  function generateCommentBubble(commentBucket) {
    if (!commentBucket) {
      return null;
    }

    const { commentCount, latestComment, ind, middleOfBucket } = commentBucket;
    const { content, avatar, user_name: userName, id: id } = latestComment;

    return (
      <CommentBubble
        avatar={avatar && avatar.thumb}
        commentBarRef={commentBarRef}
        commentCount={commentCount - 1}
        content={content}
        key={id}
        percentageLeft={timelinePosition(middleOfBucket)}
        userName={userName}
        onClick={() => handleBucketClick(ind)}
      />
    );
  }

  function timelinePosition(markerTime) {
    return percentify(markerTime, duration);
  }

  const playerInFullscreen = useSelector(selectFullscreen);

  const handleBucketClick = bucketId => {
    if (onSharePage && !playerInFullscreen) {
      dispatch(requestVideoPlayerPause());
      dispatch(updateVideoPlayerBucketId(bucketId));
    } else {
      dispatch(videoPlayerShowComments(bucketId));
    }
  };

  const commentIcons = Object.entries(commentBuckets).map(([ind, bucket]) => {
    const { commentCount, latestComment, middleOfBucket } = bucket;
    const { id } = latestComment;

    return (
      <TimelineVideoIcon
        iconClass={timelineIconClass}
        commentBarRef={commentBarRef}
        commentCount={commentCount}
        key={id}
        onMouseEnter={() => setHovered(ind)}
        onMouseLeave={() => setHovered(-1)}
        percentageLeft={timelinePosition(middleOfBucket)}
        onClick={() => handleBucketClick(ind)}
      />
    );
  });

  const isHoveringBucket = hoveredInd >= 0 && hoveredInd != activeBucketInd;

  const timeActiveClass = classNames(styles.commentBubble, {
    [styles.partialFade]: isHoveringBucket,
  });

  return (
    <CommentFetch>
      <div
        className={styles.commentBar}
        onMouseMove={props.onMouseMove}
        onMouseOut={props.onMouseOut}
        ref={newRef => setCommentBarRef(newRef)}
      >
        {commentIcons}
        <div className={styles.commentBubbleGroup}>
          <MediaQuery query={`(min-width: ${SMALL_TABLET_MIN_WIDTH}px)`}>
            <CSSTransitionGroup
              className={timeActiveClass}
              transitionName={{
                enter: styles.bubbleEnter,
                enterActive: styles.bubbleEnterActive,
                leave: styles.bubbleLeave,
                leaveActive: styles.bubbleLeaveActive,
              }}
              transitionEnterTimeout={COMMENT_BUBBLE_TRANSITION_DURATION}
              transitionLeaveTimeout={COMMENT_BUBBLE_TRANSITION_DURATION}
            >
              {generateCommentBubble(activeBucket)}
            </CSSTransitionGroup>
            <CSSTransitionGroup
              transitionName={{
                enter: styles.hoverEnter,
                enterActive: styles.hoverEnterActive,
                leave: styles.hoverLeave,
                leaveActive: styles.hoverLeaveActive,
              }}
              transitionEnterTimeout={COMMENT_BUBBLE_TRANSITION_DURATION}
              transitionLeaveTimeout={COMMENT_BUBBLE_TRANSITION_DURATION}
            >
              {hoveredInd != activeBucketInd &&
                generateCommentBubble(hoveredBucket)}
            </CSSTransitionGroup>
          </MediaQuery>
        </div>
      </div>
    </CommentFetch>
  );
};

export default CommentTimeline;
